/**
 * Print-mode plumbing shared by EmbedHost (provider) and the chart /
 * control components (consumers).
 *
 * EmbedHost calls `provide(PRINT_MODE_KEY, () => props.print === true)`
 * once in setup. Descendants call `usePrintMode()` to inject the
 * thunk and read `value()` whenever they need to decide whether to
 * render the live or static variant.
 *
 * Why a thunk and not a `Ref<boolean>`?
 *
 *   1. The `print` prop never mutates after mount in practice (the
 *      print template instantiates the element once with `print=true`
 *      and never flips it back). A thunk reading off the parent props
 *      is the same information at zero ref overhead.
 *   2. Components that mount before EmbedHost finishes its first
 *      `setup` still see `undefined` until `provide` runs; the thunk
 *      defaults to `false` so an early consumer doesn't crash.
 *
 * Keep this file dependency-free — it's imported by both EmbedHost
 * and every interactive/chart component, so a fat import here would
 * bloat the runtime bundle proportionally.
 */

import { inject, type InjectionKey } from "vue";

/** Inject key for the print-mode thunk. */
export const PRINT_MODE_KEY: InjectionKey<() => boolean> = Symbol(
  "lucent:print-mode",
);

/**
 * Read print-mode from the nearest ancestor that provided it. Returns
 * a getter so descendants that want reactivity can call it inside a
 * `computed(...)` — every component using this in a `computed` re-
 * evaluates if EmbedHost's prop changes.
 *
 * Defaults to `() => false` when no provider is found, so unit tests
 * mounting individual chart components in isolation see a sensible
 * fallback.
 */
export const usePrintMode = (): (() => boolean) =>
  inject(PRINT_MODE_KEY, () => false);
